/**
 * Aggregate CRM session heartbeats into CEO time-cost sessions and a report.
 * Placeholder $/hr rate is config-only — not an approved CFO rate.
 */
import type {
  CrmActivityClass,
  CrmSessionHeartbeat,
  EnhancedDeal,
  Lead,
  TimberProject,
} from '../types';
import { crmActivityClassLabel } from './crmActivityClass';
import { formatCrmMinutes } from './crmActivityHours';
import { leadDisplayName } from './crmActivityContext';
import type { TeamActivityDateRange } from './teamActivityReport';

/** Drop blips shorter than 15 seconds. */
export const CEO_TIME_MIN_SESSION_MS = 15 * 1000;
/** Merge adjacent heartbeats with the same attribution within this gap. */
export const CEO_TIME_SESSION_GAP_MS = 4 * 60 * 1000;
/** Cap attributed minutes between two pings (missed heartbeats / overnight). */
export const CEO_TIME_MAX_HEARTBEAT_GAP_MS = 90 * 1000;

export interface CeoTimeSession {
  userId: string;
  userEmail: string;
  userName: string;
  start: Date;
  end: Date;
  durationMinutes: number;
  jobId?: string;
  dealId?: string;
  activityClass: CrmActivityClass;
  pathSamples: string[];
  entityName?: string;
}

export interface CeoTimeJobRow {
  key: string;
  label: string;
  jobId?: string;
  dealId?: string;
  minutes: number;
  estimatedCost: number | null;
}

export interface CeoTimeClassRow {
  activityClass: CrmActivityClass;
  label: string;
  minutes: number;
  estimatedCost: number | null;
}

export interface CeoTimeCostReport {
  range: TeamActivityDateRange;
  totalMinutes: number;
  jobMinutes: number;
  untaggedMinutes: number;
  byJob: CeoTimeJobRow[];
  byClass: CeoTimeClassRow[];
  sessions: CeoTimeSession[];
  /** Null means placeholder unset — do not invent a production rate. */
  hourlyRateUsd: number | null;
  jobEstimatedCost: number | null;
  untaggedEstimatedCost: number | null;
  totalEstimatedCost: number | null;
}

function costForMinutes(minutes: number, hourlyRateUsd: number | null): number | null {
  if (hourlyRateUsd == null || !Number.isFinite(hourlyRateUsd) || hourlyRateUsd < 0) return null;
  return (minutes / 60) * hourlyRateUsd;
}

function attributionKey(hb: Pick<CrmSessionHeartbeat, 'userId' | 'jobId' | 'dealId' | 'activityClass'>): string {
  if (hb.jobId) return `job:${hb.jobId}`;
  if (hb.dealId) return `deal:${hb.dealId}`;
  return `class:${hb.activityClass || 'unknown'}`;
}

/**
 * Merge adjacent focused heartbeats for the same user + job (or class if untagged)
 * into sessions. Duration is the gap to the next ping, capped at 90s.
 */
export function aggregateCrmSessionHeartbeats(
  heartbeats: CrmSessionHeartbeat[],
  options?: { gapMs?: number; minSessionMs?: number; maxGapMs?: number },
): CeoTimeSession[] {
  const gapMs = options?.gapMs ?? CEO_TIME_SESSION_GAP_MS;
  const minSessionMs = options?.minSessionMs ?? CEO_TIME_MIN_SESSION_MS;
  const maxGapMs = options?.maxGapMs ?? CEO_TIME_MAX_HEARTBEAT_GAP_MS;

  const usable = heartbeats
    .filter((hb) => hb.userId && hb.visible !== false && hb.focused !== false)
    .slice()
    .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime() || a.userId.localeCompare(b.userId));

  type Open = {
    userId: string;
    userEmail: string;
    userName: string;
    start: Date;
    end: Date;
    jobId?: string;
    dealId?: string;
    activityClass: CrmActivityClass;
    pathSamples: string[];
    entityName?: string;
    attrKey: string;
    durationMs: number;
    heartbeatCount: number;
  };

  const sessions: CeoTimeSession[] = [];
  const openByUser = new Map<string, Open>();

  const flush = (userId: string) => {
    const open = openByUser.get(userId);
    if (!open) return;
    openByUser.delete(userId);
    let durationMs = open.durationMs;
    // A single focused ping counts as one beacon interval when no successor gap exists.
    if (durationMs <= 0 && open.heartbeatCount >= 1) durationMs = 60_000;
    if (durationMs < minSessionMs) return;
    sessions.push({
      userId: open.userId,
      userEmail: open.userEmail,
      userName: open.userName,
      start: open.start,
      end: open.end,
      durationMinutes: durationMs / 60_000,
      jobId: open.jobId,
      dealId: open.dealId,
      activityClass: open.activityClass,
      pathSamples: open.pathSamples.slice(0, 12),
      entityName: open.entityName,
    });
  };

  for (const hb of usable) {
    const key = attributionKey(hb);
    const open = openByUser.get(hb.userId);
    const ts = hb.timestamp.getTime();

    if (open) {
      const gap = ts - open.end.getTime();
      if (open.attrKey === key && gap >= 0 && gap <= gapMs) {
        const credited = Math.min(Math.max(gap, 0), maxGapMs);
        open.durationMs += credited;
        open.end = hb.timestamp;
        open.heartbeatCount += 1;
        if (hb.path && !open.pathSamples.includes(hb.path)) open.pathSamples.push(hb.path);
        if (hb.entityName) open.entityName = hb.entityName;
        if (hb.userEmail) open.userEmail = hb.userEmail;
        if (hb.userName) open.userName = hb.userName;
        continue;
      }
      flush(hb.userId);
    }

    openByUser.set(hb.userId, {
      userId: hb.userId,
      userEmail: hb.userEmail || '',
      userName: hb.userName || 'Unknown',
      start: hb.timestamp,
      end: hb.timestamp,
      jobId: hb.jobId,
      dealId: hb.dealId,
      activityClass: hb.activityClass || 'unknown',
      pathSamples: hb.path ? [hb.path] : [],
      entityName: hb.entityName,
      attrKey: key,
      // Gaps between pings accumulate; a lone heartbeat credits one interval on flush.
      durationMs: 0,
      heartbeatCount: 1,
    });
  }

  for (const userId of Array.from(openByUser.keys())) flush(userId);

  return sessions.sort((a, b) => b.start.getTime() - a.start.getTime());
}

export function buildCeoTimeCostReport(args: {
  range: TeamActivityDateRange;
  heartbeats: CrmSessionHeartbeat[];
  projects?: TimberProject[];
  leads?: Lead[];
  deals?: EnhancedDeal[];
  /** Placeholder only — leave null until Matthew/CFO sets a working rate. */
  hourlyRateUsd?: number | null;
  userId?: string;
}): CeoTimeCostReport {
  const hourlyRateUsd = args.hourlyRateUsd == null ? null : args.hourlyRateUsd;
  const projectById = new Map(
    (args.projects || []).filter((p) => p.id).map((p) => [p.id as string, p]),
  );
  const leadById = new Map(
    (args.leads || []).filter((l) => l.id).map((l) => [l.id as string, l]),
  );
  const dealById = new Map(
    (args.deals || []).filter((d) => d.id).map((d) => [d.id as string, d]),
  );

  const inRange = args.heartbeats.filter((hb) => {
    if (args.userId && hb.userId !== args.userId) return false;
    const t = hb.timestamp.getTime();
    return t >= args.range.start.getTime() && t <= args.range.end.getTime();
  });

  const sessions = aggregateCrmSessionHeartbeats(inRange);

  const jobAcc = new Map<string, CeoTimeJobRow>();
  const classAcc = new Map<CrmActivityClass, number>();
  let jobMinutes = 0;
  let untaggedMinutes = 0;

  for (const session of sessions) {
    classAcc.set(
      session.activityClass,
      (classAcc.get(session.activityClass) || 0) + session.durationMinutes,
    );

    if (session.jobId || session.dealId) {
      jobMinutes += session.durationMinutes;
      const key = session.jobId
        ? `job:${session.jobId}`
        : `deal:${session.dealId}`;
      let label = session.entityName || '';
      if (!label && session.jobId) {
        const proj = projectById.get(session.jobId);
        label = proj?.name || `Job ${session.jobId.slice(0, 8)}`;
      }
      if (!label && session.dealId) {
        const deal = dealById.get(session.dealId);
        const lead = deal?.leadId ? leadById.get(deal.leadId) : undefined;
        label = deal?.contactName
          || (lead ? leadDisplayName(lead) : '')
          || `Deal ${session.dealId.slice(0, 8)}`;
      }
      const existing = jobAcc.get(key);
      if (existing) {
        existing.minutes += session.durationMinutes;
      } else {
        jobAcc.set(key, {
          key,
          label: label || 'Tagged work',
          jobId: session.jobId,
          dealId: session.dealId,
          minutes: session.durationMinutes,
          estimatedCost: null,
        });
      }
    } else {
      untaggedMinutes += session.durationMinutes;
    }
  }

  const byJob = Array.from(jobAcc.values())
    .map((row) => ({
      ...row,
      estimatedCost: costForMinutes(row.minutes, hourlyRateUsd),
    }))
    .sort((a, b) => b.minutes - a.minutes || a.label.localeCompare(b.label));

  const byClass: CeoTimeClassRow[] = (
    ['sales', 'meetings', 'design', 'admin', 'site_visits', 'unknown'] as CrmActivityClass[]
  )
    .map((activityClass) => {
      const minutes = classAcc.get(activityClass) || 0;
      return {
        activityClass,
        label: crmActivityClassLabel(activityClass),
        minutes,
        estimatedCost: costForMinutes(minutes, hourlyRateUsd),
      };
    })
    .filter((row) => row.minutes > 0)
    .sort((a, b) => b.minutes - a.minutes || a.label.localeCompare(b.label));

  const totalMinutes = jobMinutes + untaggedMinutes;

  return {
    range: args.range,
    totalMinutes,
    jobMinutes,
    untaggedMinutes,
    byJob,
    byClass,
    sessions,
    hourlyRateUsd,
    jobEstimatedCost: costForMinutes(jobMinutes, hourlyRateUsd),
    untaggedEstimatedCost: costForMinutes(untaggedMinutes, hourlyRateUsd),
    totalEstimatedCost: costForMinutes(totalMinutes, hourlyRateUsd),
  };
}

export function formatCeoCostUsd(amount: number | null): string {
  if (amount == null || !Number.isFinite(amount)) return '—';
  return amount.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

export function buildCeoTimeCostCsv(report: CeoTimeCostReport): string {
  const lines = [
    ['Section', 'Name', 'Hours', 'Minutes', 'Est. cost (placeholder)'].join(','),
  ];
  const cell = (value: string | number) => {
    const text = String(value ?? '');
    if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
    return text;
  };
  const push = (cols: Array<string | number>) => {
    lines.push(cols.map(cell).join(','));
  };

  push([
    'Total',
    'CRM time',
    formatCrmMinutes(report.totalMinutes),
    Math.round(report.totalMinutes),
    report.totalEstimatedCost == null ? '' : report.totalEstimatedCost.toFixed(2),
  ]);
  push([
    'Jobs',
    'Tagged',
    formatCrmMinutes(report.jobMinutes),
    Math.round(report.jobMinutes),
    report.jobEstimatedCost == null ? '' : report.jobEstimatedCost.toFixed(2),
  ]);
  push([
    'Overhead',
    'Untagged',
    formatCrmMinutes(report.untaggedMinutes),
    Math.round(report.untaggedMinutes),
    report.untaggedEstimatedCost == null ? '' : report.untaggedEstimatedCost.toFixed(2),
  ]);

  for (const row of report.byJob) {
    push([
      'By job',
      row.label,
      formatCrmMinutes(row.minutes),
      Math.round(row.minutes),
      row.estimatedCost == null ? '' : row.estimatedCost.toFixed(2),
    ]);
  }
  for (const row of report.byClass) {
    push([
      'By class',
      row.label,
      formatCrmMinutes(row.minutes),
      Math.round(row.minutes),
      row.estimatedCost == null ? '' : row.estimatedCost.toFixed(2),
    ]);
  }
  return `${lines.join('\n')}\n`;
}

export async function fetchCeoTimeCostSources(range: TeamActivityDateRange, userId?: string) {
  const { fetchCrmSessionHeartbeatsInRange } = await import('./crmSessionHeartbeatService');
  const heartbeats = await fetchCrmSessionHeartbeatsInRange({
    start: range.start,
    end: range.end,
    userId,
  });
  return { heartbeats };
}
